import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * In-app confirm dialog (replaces window.confirm) so the prompt stays until OK / Cancel
 * and is not disrupted by keyup / pointer events from the test UI underneath.
 */
export function useBlockingConfirm() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(null);
  const resolveRef = useRef(null);
  const openRef = useRef(false);

  const confirmAsync = useCallback((message) => {
    return new Promise((resolve) => {
      openRef.current = true;
      resolveRef.current = resolve;
      setPrompt({ message });
    });
  }, []);

  const finish = useCallback((value) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    openRef.current = false;
    setPrompt(null);
    if (r) r(!!value);
  }, []);

  useEffect(() => {
    if (!prompt) return undefined;
    const onKeyDownCapture = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      finish(false);
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [prompt, finish]);

  const ConfirmDialog =
    prompt &&
    createPortal(
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center bg-black/45 px-4"
        role="presentation"
        aria-hidden
      >
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="blocking-confirm-message"
          className="max-w-md w-full rounded-xl bg-surface shadow-xl border border-edge p-6"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <p
            id="blocking-confirm-message"
            className="text-ink text-justify mb-6 whitespace-pre-wrap"
          >
            {prompt.message}
          </p>
          <div className="flex justify-end flex-wrap gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-edge-strong text-ink hover:bg-surface-muted"
              onClick={() => finish(false)}
            >
              {t('test.confirmCancel')}
            </button>
            <button
              type="button"
              autoFocus
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover"
              onClick={() => finish(true)}
            >
              {t('test.confirmOk')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  const isConfirmOpen = useCallback(() => openRef.current, []);

  return {
    confirmAsync,
    isConfirmOpen,
    ConfirmDialog
  };
}
